import { Injectable, NgZone } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, share } from 'rxjs';
import { CookieService } from './cookie.service';

@Injectable({
    providedIn: 'root'
})
export class RuntimeService {
    private readonly BUILD_NAME: string = 'build';
    private readonly DATABASE_PATH: string = 'build';

    protected subjectRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected firstRun: boolean = true;

    constructor(
        private cookieService: CookieService,
        private ngZone: NgZone,
    ) {
        if (!this.cookieService.isSupported()) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.watchBuild().catch(() => {
                this.firstRun = false;
            });
        });
    }

    protected async watchBuild(): Promise<void> {
        const [{ getApp, getApps, initializeApp }, { getDatabase, ref, onValue }] = await Promise.all([
            import('firebase/app'),
            import('firebase/database'),
        ]);

        const build = this.getBuild();
        const app = getApps().length ? getApp() : initializeApp(environment.services.firebase);
        const db = getDatabase(app);
        const reference = ref(db, `${this.DATABASE_PATH}/${environment?.name}`);

        onValue(reference, (snapshot) => {
            const buildSnapshot = '' + (snapshot.val() ?? '');

            if (build !== buildSnapshot) {
                const buildUnset: boolean = !build || !build?.length;

                try {
                    this.cookieService.set(this.BUILD_NAME, buildSnapshot ?? '', 365, '/');
                } catch (e) { }

                if (!buildUnset && !this.firstRun) {
                    this.ngZone.run(() => this.subjectRefresh.next(true));
                }
            }

            this.firstRun = false;
        });

        setTimeout(() => {
            this.firstRun = false;
        }, 5000);
    }

    getBuild(): string {
        if (!this.cookieService.isSupported()) {
            return '';
        }
        return this.cookieService.get(this.BUILD_NAME) ?? '';
    }

    /**
     * Returns status for refresh
     */
    askRefresh(): Observable<boolean> {
        return this.subjectRefresh.asObservable().pipe(share());
    }
}
